import axios from 'axios'

/**
 * Two backends:
 *   - `api`   → granguard-api: read-only content (articles, scams). The
 *               GG production database is the source of truth.
 *   - `sdApi` → scamdefense-api: anonymous engagement & feedback for
 *               scamdefense.org. Owns its own Neon database. Sets the
 *               `sd_session` cookie used for tracking.
 *
 * Both are public-only (no auth headers). Override at deploy time via
 * VITE_API_URL and VITE_SCAMDEFENSE_API_URL.
 */
export const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'https://granguard-api.vercel.app/api/v1'

export const SCAMDEFENSE_API_BASE_URL =
  import.meta.env.VITE_SCAMDEFENSE_API_URL ||
  'https://scamdefense-api.vercel.app/api/v1'

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15_000,
})

/**
 * scamdefense-api client. `withCredentials: true` is required so the
 * browser sends the `sd_session` cookie on cross-site XHR.
 */
export const sdApi = axios.create({
  baseURL: SCAMDEFENSE_API_BASE_URL,
  timeout: 15_000,
  withCredentials: true,
})

export type Article = {
  id: string
  slug: string
  title: string
  /** Short blurb. Some endpoints return this as `description`, some as `excerpt`. */
  description?: string | null
  excerpt?: string | null
  /** Long-form HTML body. Render with dangerouslySetInnerHTML. */
  content?: string | null
  /** Card-sized image URL on the granguard-api Article model. */
  thumbnail?: string | null
  /** Hero/banner image URL on the granguard-api Article model. */
  banner?: string | null
  /** Some clients use this name; we accept it as a fallback. */
  coverImageUrl?: string | null
  readTime?: number | null
  featured?: boolean | null
  articleType?: string | null
  publishedAt?: string | null
  market?: string | null
  category?: { name: string; slug: string } | null
  tags?: { name: string; slug: string }[] | null
  /** Arabic translation for the bilingual (Qatar) face; local articles only. */
  ar?: { title: string; description?: string | null; content?: string | null }
}

/** Pick the best image URL from whatever the API returned. */
export function articleImage(a: Article): string | null {
  return a.thumbnail || a.banner || a.coverImageUrl || null
}

/** Normalize the short-blurb field across `description`/`excerpt` variants. */
export function articleBlurb(a: Article): string | null {
  return a.description || a.excerpt || null
}

export type Scam = {
  id: string
  /** Optional because some legacy entries may lack a slug. */
  slug?: string | null
  title: string
  description?: string | null
  /** Long-form body. Markdown / HTML depending on CMS. */
  content?: string | null
  /** Lucide-style icon name from the CMS. */
  icon?: string | null
  /** Hero/cover image URL. */
  image?: string | null
  market?: string | null
  riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | null
  channels?: string[] | null
  scamCategory?: { id: string; name: string } | null
  tags?: { id: string; name: string }[] | null
}

export async function fetchPublicArticles(params?: {
  market?: string
  limit?: number
}): Promise<Article[]> {
  const { data } = await api.get('/public/articles', {
    params: { market: params?.market ?? 'US', limit: params?.limit ?? 9 },
  })
  if (Array.isArray(data)) return data as Article[]
  if (Array.isArray(data?.articles)) return data.articles as Article[]
  return []
}

export async function fetchPublicArticle(slug: string): Promise<Article | null> {
  try {
    const { data } = await api.get(`/public/articles/${slug}`, {
      params: { market: 'US' },
    })
    return (data?.article ?? data) as Article
  } catch {
    return null
  }
}

/**
 * NOTE: granguard-api currently does NOT expose a public `/public/scams`
 * endpoint, only `/app/scams` (auth-required) and superadmin. We need
 * granguard-api to add public scam routes before the Scam Library page
 * has real data.
 *
 * For now these calls 404 and the UI falls back to empty/error states.
 * Tracking this in TRADEOFFS.md.
 */
export type Charity = {
  name: string
  registrationNumber: string
  status: 'registered' | 'removed' | 'unknown'
  country: string
  city?: string
  state?: string
  street?: string
  zip?: string
  income?: number
  assets?: number
  revenue?: number
  nteeCode?: string
  yearsActive?: number
  registeredDate?: string
  subsectionCode?: number
  isPublicCharity501c3?: boolean
  donationsTaxDeductible?: boolean
  latestFilingYear?: number
  filingsLookStale?: boolean
  /** From the charity's own Form 990 via our registry, when we hold it. */
  website?: string
  websiteHost?: string
  phone?: string
  contactTaxYear?: number
  revoked?: boolean
  revocationDate?: string
  provider?: 'registry' | 'propublica'
  source: string
  sourceUrl: string
}

export type CharitySearchResponse = {
  query: string
  market: string
  results: Charity[]
  /** How many the register reports matching; may far exceed results.length. */
  totalMatches?: number
  provider?: 'registry' | 'propublica'
  message?: string
}

/**
 * Public charity lookup. Unauthenticated route on granguard-api, which is
 * already this site's backend for scams and articles. US by default: the UK
 * Charity Commission API needs a subscription key we do not have.
 */
export async function searchCharities(
  q: string,
  market = 'US',
): Promise<CharitySearchResponse> {
  const { data } = await api.get('/public/charity/search', { params: { q, market } })
  return data as CharitySearchResponse
}

export async function fetchPublicScams(params?: {
  market?: string
}): Promise<Scam[]> {
  const { data } = await api.get('/public/scams', {
    params: { market: params?.market ?? 'US' },
  })
  if (Array.isArray(data)) return data as Scam[]
  if (Array.isArray(data?.scams)) return data.scams as Scam[]
  return []
}

export async function fetchPublicScam(slug: string): Promise<Scam | null> {
  try {
    const { data } = await api.get(`/public/scams/${slug}`, {
      params: { market: 'US' },
    })
    return (data?.scam ?? data) as Scam
  } catch {
    return null
  }
}
